import { ComponentProps, JSX } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLinkTo } from '@react-navigation/native';
import { useDeviceSize, DEVICE_SIZES } from 'rn-responsive-styles';
import { ListRenderItem } from '@shopify/flash-list';

import { Text, FlashList, spacing } from '@pletly/ui';
import { useUser } from '%hooks';
import { Group } from './Group';
import { GroupButton } from './GroupButton';

type Group = {
	id: string;
	uuid: string;
	name: string;
	description?: string | null;
	inviteId?: string;
	ownerId: string;
	members: ComponentProps<typeof Group>['members'];
};

interface Props<GroupData extends Group> {
	title?: string;
	groups: GroupData[];
	onJoinPress?: ((inviteId: string, groupUuid?: string) => void) | ((groupId: string) => void);
	refreshControl?: JSX.Element | undefined;
}

const COLUMNS = {
	[DEVICE_SIZES.SMALL_DEVICE.toString()]: 1,
	[DEVICE_SIZES.MEDIUM_DEVICE.toString()]: 2,
	['default']: 4,
} as const;

export const GroupsList = <GroupData extends Group>({
	title,
	groups,
	onJoinPress,
	refreshControl,
}: Props<GroupData>) => {
	const { t } = useTranslation();
	const navigate = useLinkTo();
	const user = useUser();
	const deviceSize = useDeviceSize();
	const numColumns = COLUMNS[deviceSize] || COLUMNS['default'];

	const renderItem: ListRenderItem<GroupData> | null | undefined = ({ item, index }) => {
		const isOwner = item.ownerId === user.id;
		const isMemberAlready = item.members.map((member) => member.id).includes(user.id);
		const onPress = isMemberAlready ? () => navigate(`/communities/${item.uuid}`) : undefined;
		const onJoin =
			onJoinPress && !isMemberAlready
				? () => (item.inviteId ? onJoinPress(item.inviteId, item.uuid) : onJoinPress(item.id))
				: undefined;
		const ctaButton =
			isMemberAlready || isOwner ? (
				<GroupButton color="basic" onPress={onPress}>
					{isOwner ? t('community.join-owner') : t('community.joined')}
				</GroupButton>
			) : undefined;
		const isLastItemInRow = (index + 1) % numColumns === 0;

		return (
			<Group
				style={[styles.row, !isLastItemInRow && styles.itemGap]}
				name={item.name}
				description={item.description}
				ctaButton={ctaButton}
				onPress={onPress}
				onJoinPress={onJoin}
				members={item.members}
			/>
		);
	};

	const Header = <Text category="h3">{title}</Text>;

	return (
		<View style={styles.root}>
			<FlashList
				key={`communities-${numColumns}-columns`}
				ListHeaderComponent={Header}
				data={groups}
				refreshControl={refreshControl}
				keyExtractor={(group, index) => `${group.uuid}-${index}`}
				estimatedItemSize={232}
				renderItem={renderItem}
				numColumns={numColumns}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		flex: 1,
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'stretch',
		gap: spacing(2),
	},
	itemGap: {
		marginRight: spacing(3),
	},
	row: {
		marginTop: spacing(3),
	},
});
